import { z } from "zod";

import { ALL_COUNTRY_CODES } from "@garimpa/lib/countries";

const pendingPhoneVerificationSchema = z.object({
  country: z.enum(ALL_COUNTRY_CODES),
  phone: z.string().trim().min(1).max(32),
  resendAvailableAt: z.number().int().nonnegative(),
});

export type PendingPhoneVerification = z.infer<typeof pendingPhoneVerificationSchema>;

const STORAGE_KEY_PREFIX = "garimpa:pending-phone-verification:";

function getSessionStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(scope: string) {
  return `${STORAGE_KEY_PREFIX}${scope}`;
}

export function readPendingPhoneVerification(scope?: string) {
  if (!scope) return null;

  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(storageKey(scope));
    if (!raw) return null;

    const parsed = pendingPhoneVerificationSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;

    clearPendingPhoneVerification(scope);
    return null;
  } catch {
    clearPendingPhoneVerification(scope);
    return null;
  }
}

export function writePendingPhoneVerification(
  scope: string | undefined,
  value: PendingPhoneVerification,
) {
  if (!scope) return;

  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(storageKey(scope), JSON.stringify(pendingPhoneVerificationSchema.parse(value)));
  } catch {
    // Storage quota and privacy-mode failures must not block phone verification.
  }
}

export function clearPendingPhoneVerification(scope?: string) {
  if (!scope) return;

  try {
    getSessionStorage()?.removeItem(storageKey(scope));
  } catch {
    // Storage failures must not block verification cleanup.
  }
}
